import { useState, useEffect } from 'react'
import { Sparkles, Wand2, Images, Layers, Mic, FileText, Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'

interface NavbarProps {
  activeSection: string
  onNavigate: (section: string) => void
}

export function Navbar({ activeSection, onNavigate }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const navItems = [
    { label: 'Studio', href: 'studio', icon: Wand2 },
    { label: 'Gallery', href: 'gallery', icon: Images },
    { label: 'Features', href: 'features', icon: Layers },
  ]

  return (
    <nav
      className={cn(
        'fixed top-0 left-0 right-0 z-50 px-6 py-4 flex items-center justify-between transition-all duration-300',
        scrolled
          ? 'bg-background/90 backdrop-blur-xl border-b border-border py-3'
          : 'bg-transparent'
      )}
    >
      <button
        onClick={() => onNavigate('home')}
        className="flex items-center gap-3 group"
      >
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary via-secondary to-accent flex items-center justify-center text-white shadow-lg shadow-primary/20 group-hover:shadow-primary/40 transition-shadow">
          <Sparkles className="w-5 h-5" />
        </div>
        <div className="font-display text-xl font-bold">
          Lumina<span className="text-primary">AI</span>
          <span className="text-xs text-muted-foreground font-normal ml-2 hidden sm:inline">Studio</span>
        </div>
      </button>

      {/* Desktop Nav */}
      <div className="hidden md:flex items-center gap-2">
        {navItems.map((item) => (
          <button
            key={item.href}
            onClick={() => onNavigate(item.href)}
            className={cn(
              'px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2',
              activeSection === item.href
                ? 'text-primary bg-primary/10'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
            )}
          >
            <item.icon className="w-4 h-4" />
            {item.label}
          </button>
        ))}
        <button
          onClick={() => onNavigate('studio')}
          className="ml-2 px-5 py-2.5 rounded-lg bg-gradient-to-r from-primary to-primary/80 text-white text-sm font-semibold shadow-lg shadow-primary/20 hover:shadow-primary/40 hover:-translate-y-0.5 transition-all flex items-center gap-2"
        >
          <Wand2 className="w-4 h-4" />
          Create
        </button>
      </div>

      {/* Mobile Toggle */}
      <button
        onClick={() => setMobileOpen(!mobileOpen)}
        className="md:hidden p-2 rounded-lg hover:bg-muted transition-colors"
      >
        {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="absolute top-full left-0 right-0 bg-background/95 backdrop-blur-xl border-b border-border p-4 md:hidden flex flex-col gap-2">
          {navItems.map((item) => (
            <button
              key={item.href}
              onClick={() => {
                onNavigate(item.href)
                setMobileOpen(false)
              }}
              className={cn(
                'px-4 py-3 rounded-lg text-sm font-medium transition-all flex items-center gap-3',
                activeSection === item.href
                  ? 'text-primary bg-primary/10'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              )}
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </button>
          ))}
        </div>
      )}
    </nav>
  )
}
